/**
 * Instance counter shared by every RowExpansion in the app, subclasses included. Two tables of
 * the same kind on one page pass the same panel-id prefix, so uniqueness has to come from here
 * rather than from the prefix.
 */
let nextInstanceId = 0;

/**
 * Which rows of a table are open, the DOM id of each row's detail panel, and the label shown
 * beside each chevron. Held as a plain class rather than a service so each table owns its own
 * instance — two expandable cards can be on screen together and must not share expansion state.
 *
 * The table still owns the feature flag and the `expand` column, since both touch component
 * state this class has no business reaching into.
 *
 * Generic over the row type and over what a row counts: the caller supplies the accessor and
 * the noun, so the same expansion behaviour serves the billing cards' task breakdown and the
 * assignments card's task assignments without either knowing about the other's row shape.
 */
export class RowExpansion<TRow extends object> {
  /** Distinguishes this table's panel ids from every other expandable table on the page. */
  private readonly instanceId = nextInstanceId++;

  /**
   * Rows currently open. Any number can be open at once, so panels can be compared side by
   * side. Keyed by row identity rather than index, so sorting and paging preserve which rows
   * are open within a result set.
   */
  private readonly expanded = new Set<TRow>();

  /**
   * Stable DOM ids for each row's panel, so the toggle can point `aria-controls` at what it
   * opens. Keyed off row identity rather than derived from row data: an id built from, say,
   * employee plus bill type would be unique today but would start colliding the moment the row
   * grain changes. A WeakMap also lets the entries go when the rows do.
   */
  private readonly panelIds = new WeakMap<TRow, string>();
  private nextPanelId = 0;

  /**
   * @param countOf How many items a row's panel previews. Drives {@link count} and the label.
   * @param noun Singular noun for the label; the plural is it plus an "s" ("1 task"/"3 tasks").
   * @param panelIdPrefix Leading segment of every panel id this instance hands out. Required
   *   rather than defaulted so each table's ids are recognisable in the DOM.
   */
  constructor(
    private readonly countOf: (row: TRow) => number,
    private readonly noun: string,
    private readonly panelIdPrefix: string,
  ) {}

  /** Toggles a row's panel. Re-clicking an open row closes it. */
  toggle(row: TRow): void {
    if (this.expanded.has(row)) {
      this.expanded.delete(row);
    } else {
      this.expanded.add(row);
    }
  }

  isExpanded(row: TRow): boolean {
    return this.expanded.has(row);
  }

  /**
   * Assigns each row its panel id. Called once when a result set is handed to the table, so
   * that {@link panelId} stays a pure read — it is bound in the template, and a binding that
   * writes state during change detection is a trap even when, as here, it is idempotent.
   */
  prepare(rows: readonly TRow[]): void {
    for (const row of rows) {
      this.panelIds.set(
        row,
        `${this.panelIdPrefix}-${this.instanceId}-${this.nextPanelId++}`,
      );
    }
  }

  /** The DOM id of a row's panel. Empty for a row that was never prepared. */
  panelId(row: TRow): string {
    return this.panelIds.get(row) ?? '';
  }

  /**
   * Drops every expansion. Call on each fetch: the entries are row objects, and a fetch
   * discards them all — without this they would be retained for the component's lifetime.
   * Panel ids are deliberately left alone; they belong to the rows, which the WeakMap releases
   * on its own once the rows go.
   */
  clear(): void {
    this.expanded.clear();
  }

  /**
   * How many items a row's panel previews, per the accessor the caller supplied. Zero covers
   * two different rows — one nothing was reported about, and one reported as having none — and
   * both still open, onto a single explanatory line.
   */
  count(row: TRow): number {
    return this.countOf(row);
  }

  /**
   * Count label for the expander, e.g. "3 tasks". Empty at zero — the row still expands, but
   * onto an explanatory line rather than items, and "0 tasks" beside a chevron that opens to
   * content would be the more confusing of the two.
   */
  label(row: TRow): string {
    const count = this.count(row);
    if (count === 0) {
      return '';
    }
    return count === 1 ? `1 ${this.noun}` : `${count} ${this.noun}s`;
  }
}
